import React, { Component } from 'react';
import { DetailsList, DetailsListLayoutMode, IColumn, SelectionMode, Selection, Spinner, SpinnerSize, MessageBar, MessageBarType, CommandButton, SearchBox, Toggle, TextField, DefaultButton, PrimaryButton } from '@fluentui/react';
import { IPersonaProps } from '@fluentui/react';
import { IGroup } from '@microsoft/mgt-react';
import { checkUserMembershipInGroup, getGroupMemberCount, getGroupsByName, getMembersOfGroup, getUserInfoByIds } from '../../../../../lib/graph';
import NoRecordsFound from '../../../../../core/components/NoRecordsFound';
import Template, { BeautyPerson } from '../../../../../core/libraries/Template';

interface IProps {
    // Define your props here
}

interface IGroupWithMembers {
    group: IGroup;
    members: IPersonaProps[];
    memberCount: number;
    actualMemberCount?: number; // For large groups, actual count from API
    isLoading: boolean;
    error?: string;
    isLargeGroup?: boolean; // Flag for groups we don't load members
}

interface ICachedData {
    groups: IGroupWithMembers[];
    timestamp: number;
    version: string;
}

export interface IActualUserHasRoleTableState {
    groups: IGroupWithMembers[];
    filteredGroups: IGroupWithMembers[];
    isLoadingGroups: boolean;
    error: string | null;
    selectedGroup: IGroupWithMembers | null;
    lastUpdated: Date | null;
    isUsingCache: boolean;
    searchText: string;
    groupNameSearchText: string;
    isUserSearchEnabled: boolean;
    searchResults: {
        foundUsers: Array<{
            user: IPersonaProps;
            groups: IGroupWithMembers[];
        }>;
        hasSearch: boolean;
    };
    // New states for large group search
    isLargeGroupSearchEnabled: boolean;
    largeGroupSearchText: string;
    isSearchingLargeGroup: boolean;
    largeGroupSearchResult: {
        searchedUser: string;
        isMember: boolean | null;
        hasSearched: boolean;
    };
}

class ActualUserHasRoleTable extends Component<IProps, IActualUserHasRoleTableState> {
    private readonly CACHE_KEY = 'feber_groups_cache';
    private readonly CACHE_DURATION = 24 * 60 * 60 * 1000; // 24hours in milliseconds
    private readonly CACHE_VERSION = '1.1'; // Increment this to invalidate old cache
    private readonly LARGE_GROUP_THRESHOLD = 1000; // Groups with more than 1000 members are considered large
    private readonly LARGE_GROUP_NAMES = ['IDM2BCD_feber_User']; // Groups to treat as large

    constructor(props: IProps) {
        super(props);

        this.state = {
            groups: [],
            filteredGroups: [],
            isLoadingGroups: false,
            error: null,
            selectedGroup: null,
            lastUpdated: null,
            isUsingCache: false,
            searchText: '',
            groupNameSearchText: '',
            isUserSearchEnabled: false,
            searchResults: {
                foundUsers: [],
                hasSearch: false
            },
            isLargeGroupSearchEnabled: false,
            largeGroupSearchText: '',
            isSearchingLargeGroup: false,
            largeGroupSearchResult: {
                searchedUser: '',
                isMember: null,
                hasSearched: false
            }
        };
    }

    async componentDidMount() {
        // Try to load from cache first
        const cachedData = this.loadFromCache();
        if (cachedData) {
            this.setState({
                groups: cachedData.groups,
                filteredGroups: cachedData.groups,
                lastUpdated: new Date(cachedData.timestamp),
                isUsingCache: true
            });
        } else {
            // If no cache, load fresh data
            await this.loadFeberGroups(false);
        }
    }

    private loadFromCache = (): ICachedData | null => {
        try {
            const cached = localStorage.getItem(this.CACHE_KEY);
            if (!cached) return null;

            const cachedData: ICachedData = JSON.parse(cached);
            const now = Date.now();

            // Check if cache is expired or version mismatch
            if (
                now - cachedData.timestamp > this.CACHE_DURATION ||
                cachedData.version !== this.CACHE_VERSION
            ) {
                this.clearCache();
                return null;
            }

            // Reset loading states for cached data
            const groups = cachedData.groups.map(group => ({
                ...group,
                isLoading: false
            }));

            return {
                ...cachedData,
                groups
            };
        } catch (error) {
            console.error('Error loading from cache:', error);
            this.clearCache();
            return null;
        }
    };

    private saveToCache = (groups: IGroupWithMembers[]) => {
        try {
            const cacheData: ICachedData = {
                groups: groups.map(group => ({
                    ...group,
                    isLoading: false // Don't cache loading states
                })),
                timestamp: Date.now(),
                version: this.CACHE_VERSION
            };

            localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheData));
        } catch (error) {
            console.error('Error saving to cache:', error);
            // If localStorage is full or disabled, continue without caching
        }
    };

    private clearCache = () => {
        try {
            localStorage.removeItem(this.CACHE_KEY);
        } catch (error) {
            console.error('Error clearing cache:', error);
        }
    };

    private isLargeGroup = (groupName: string): boolean => {
        return this.LARGE_GROUP_NAMES.some(name =>
            groupName.toLowerCase().includes(name.toLowerCase())
        );
    };

    private loadFeberGroups = async (useCache: boolean = true) => {
        this.setState({
            isLoadingGroups: true,
            error: null,
            isUsingCache: false
        });

        try {
            // If using cache and cache exists, use it
            if (useCache) {
                const cachedData = this.loadFromCache();
                if (cachedData) {
                    this.setState({
                        groups: cachedData.groups,
                        filteredGroups: cachedData.groups,
                        isLoadingGroups: false,
                        lastUpdated: new Date(cachedData.timestamp),
                        isUsingCache: true
                    });
                    return;
                }
            }

            // Search for groups with prefix "idm2bcd_feber"
            const groups = await getGroupsByName('idm2bcd_feber');

            if (!groups || groups.length === 0) {
                this.setState({
                    groups: [],
                    filteredGroups: [],
                    isLoadingGroups: false,
                    error: 'No groups found with prefix "idm2bcd_feber"',
                    lastUpdated: new Date()
                });
                return;
            }

            // Initialize groups with loading state
            const groupsWithMembers: IGroupWithMembers[] = groups.map((group: IGroup) => ({
                group,
                members: [],
                memberCount: 0,
                isLoading: true,
                isLargeGroup: this.isLargeGroup(group.displayName || '')
            }));

            this.setState({
                groups: groupsWithMembers,
                filteredGroups: groupsWithMembers,
                isLoadingGroups: false
            });

            // Load members for each group
            await this.loadMembersForAllGroups(groupsWithMembers);

        } catch (error) {
            console.error('Error loading FEBER groups:', error);
            this.setState({
                isLoadingGroups: false,
                error: 'Failed to load groups. Please try again.',
                lastUpdated: new Date()
            });
        }
    };

    private loadMembersForAllGroups = async (groups: IGroupWithMembers[]) => {
        const updatedGroups = [...groups];

        for (let i = 0; i < updatedGroups.length; i++) {
            try {
                const group = updatedGroups[i];

                if (group.isLargeGroup) {
                    // For large groups, just get the member count
                    const actualCount = await getGroupMemberCount(group.group.id!);
                    updatedGroups[i] = {
                        ...group,
                        members: [], // Don't load actual members
                        memberCount: 0,
                        actualMemberCount: actualCount,
                        isLoading: false
                    };
                } else {
                    // For normal groups, load members as before
                    const members = await getMembersOfGroup(group.group.id!);

                    if (members && members.length > 0) {
                        // Extract user IDs and get user info
                        const userIds = members
                            .filter((member: any) => member['@odata.type'] === '#microsoft.graph.user')
                            .map((member: any) => member.userPrincipalName || member.id);

                        const userInfo = await getUserInfoByIds(userIds);

                        updatedGroups[i] = {
                            ...group,
                            members: userInfo || [],
                            memberCount: userInfo?.length || 0,
                            isLoading: false
                        };
                    } else {
                        updatedGroups[i] = {
                            ...group,
                            members: [],
                            memberCount: 0,
                            isLoading: false
                        };
                    }
                }
            } catch (error) {
                console.error(`Error loading members for group ${updatedGroups[i].group.displayName}:`, error);
                updatedGroups[i] = {
                    ...updatedGroups[i],
                    isLoading: false,
                    error: 'Failed to load members'
                };
            }

            // Update state after each group to show progressive loading
            this.setState({
                groups: [...updatedGroups],
                filteredGroups: this.filterGroupsBySearch([...updatedGroups], this.state.searchText, this.state.groupNameSearchText)
            });
        }

        // Save to cache when all groups are loaded
        const allLoaded = updatedGroups.every(group => !group.isLoading);
        if (allLoaded) {
            this.saveToCache(updatedGroups);
            this.setState({
                lastUpdated: new Date(),
                isUsingCache: false
            });
        }
    };

    private onSearchChange = (newValue: string | undefined) => {
        const searchText = newValue || '';
        this.setState({
            searchText,
            filteredGroups: this.filterGroupsBySearch(this.state.groups, searchText, this.state.groupNameSearchText),
            searchResults: this.performUserSearch(this.state.groups, searchText)
        });
    };

    private onGroupNameSearchChange = (newValue: string | undefined) => {
        const groupNameSearchText = newValue || '';
        this.setState({
            groupNameSearchText,
            filteredGroups: this.filterGroupsBySearch(this.state.groups, this.state.searchText, groupNameSearchText)
        });
    };

    private searchLargeGroupMembership = async () => {
        const { largeGroupSearchText, groups } = this.state;

        if (!largeGroupSearchText.trim()) return;

        this.setState({ isSearchingLargeGroup: true });

        try {
            // Find the IDM2BCD_feber_User group
            const largeGroup = groups.find(g => this.isLargeGroup(g.group.displayName || ''));

            if (!largeGroup) {
                this.setState({
                    largeGroupSearchResult: {
                        searchedUser: largeGroupSearchText,
                        isMember: null,
                        hasSearched: true
                    },
                    isSearchingLargeGroup: false
                });
                return;
            }

            const isMember = await checkUserMembershipInGroup(
                largeGroup.group.id!,
                largeGroupSearchText.trim()
            );

            this.setState({
                largeGroupSearchResult: {
                    searchedUser: largeGroupSearchText,
                    isMember,
                    hasSearched: true
                },
                isSearchingLargeGroup: false
            });

        } catch (error) {
            console.error('Error searching large group membership:', error);
            this.setState({
                largeGroupSearchResult: {
                    searchedUser: largeGroupSearchText,
                    isMember: null,
                    hasSearched: true
                },
                isSearchingLargeGroup: false
            });
        }
    };

    private filterGroupsBySearch = (groups: IGroupWithMembers[], userSearchText: string, groupNameSearchText: string): IGroupWithMembers[] => {
        let filtered = groups;

        // Filter by group name if provided
        if (groupNameSearchText) {
            const searchLower = groupNameSearchText.toLowerCase();
            filtered = filtered.filter(group =>
                group.group.displayName?.toLowerCase().includes(searchLower)
            );
        }

        // Filter by user/member name if provided
        if (userSearchText) {
            const searchLower = userSearchText.toLowerCase();
            filtered = filtered.map(group => {
                // Skip large groups in user search
                if (group.isLargeGroup) {
                    return null;
                }

                // Only search by member names
                const filteredMembers = group.members.filter(member =>
                    member.text?.toLowerCase().includes(searchLower) ||
                    member.secondaryText?.toLowerCase().includes(searchLower)
                );

                if (filteredMembers.length > 0) {
                    return {
                        ...group,
                        members: filteredMembers,
                        memberCount: filteredMembers.length
                    };
                }

                return null;
            }).filter((group): group is IGroupWithMembers => group !== null);
        }

        return filtered;
    };

    private performUserSearch = (groups: IGroupWithMembers[], searchText: string) => {
        if (!searchText.trim()) {
            return { foundUsers: [], hasSearch: false };
        }

        const searchLower = searchText.toLowerCase();
        const foundUsers: Array<{
            user: IPersonaProps;
            groups: IGroupWithMembers[];
        }> = [];

        // Create a map to track unique users and their groups
        const userMap = new Map<string, {
            user: IPersonaProps;
            groups: IGroupWithMembers[];
        }>();

        // Only search in non-large groups
        groups.filter(group => !group.isLargeGroup).forEach(group => {
            group.members.forEach(member => {
                const matchesSearch = member.text?.toLowerCase().includes(searchLower) ||
                    member.secondaryText?.toLowerCase().includes(searchLower);

                if (matchesSearch) {
                    const userId = member.secondaryText || member.text || '';

                    if (userMap.has(userId)) {
                        userMap.get(userId)!.groups.push(group);
                    } else {
                        userMap.set(userId, {
                            user: member,
                            groups: [group]
                        });
                    }
                }
            });
        });

        return {
            foundUsers: Array.from(userMap.values()),
            hasSearch: true
        };
    };

    private getCacheAge = (): string => {
        const { lastUpdated } = this.state;
        if (!lastUpdated) return '';

        const now = new Date();
        const diffMs = now.getTime() - lastUpdated.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMins / 60);

        if (diffMins < 1) return 'just now';
        if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
        if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
        return lastUpdated.toLocaleDateString();
    };

    // Columns for the main groups table
    private groupColumns: IColumn[] = [
        {
            key: 'groupName',
            name: 'Group Name',
            ariaLabel: 'Group Name',
            minWidth: 250,
            maxWidth: 400,
            onColumnClick: undefined,
            onRender: (item: IGroupWithMembers) => {
                return (
                    <div style={{ padding: '8px 0' }}>
                        <div style={{ fontWeight: '600', fontSize: '14px' }}>
                            {item.group.displayName}
                            {item.isLargeGroup && (
                                <span style={{
                                    marginLeft: '8px',
                                    backgroundColor: '#fff4ce',
                                    color: '#8a6914',
                                    padding: '2px 6px',
                                    borderRadius: '8px',
                                    fontSize: '10px',
                                    fontWeight: 'normal'
                                }}>
                                    LARGE GROUP
                                </span>
                            )}
                        </div>
                        {item.group.description && (
                            <div style={{
                                fontSize: '12px',
                                color: '#666',
                                marginTop: '4px',
                                fontStyle: 'italic'
                            }}>
                                {item.group.description}
                            </div>
                        )}
                    </div>
                );
            }
        },
        {
            key: 'memberCount',
            name: 'Count',
            ariaLabel: 'Number of members',
            minWidth: 80,
            maxWidth: 80,
            onColumnClick: undefined,
            onRender: (item: IGroupWithMembers) => {
                if (item.isLoading) {
                    return <Spinner size={SpinnerSize.xSmall} />;
                }

                const displayCount = item.isLargeGroup
                    ? (item.actualMemberCount || 0)
                    : item.memberCount;

                return (
                    <span style={{
                        fontWeight: '600',
                        color: displayCount > 0 ? '#107c10' : '#666'
                    }}>
                        {displayCount.toLocaleString()}
                        {item.isLargeGroup && (
                            <div style={{ fontSize: '10px', color: '#666', fontWeight: 'normal' }}>
                                (Count only)
                            </div>
                        )}
                    </span>
                );
            }
        },
        {
            key: 'members',
            name: 'Users in Group',
            ariaLabel: 'Users in Group',
            minWidth: 400,
            maxWidth: 400,
            onColumnClick: undefined,
            onRender: (item: IGroupWithMembers) => {
                if (item.isLoading) {
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Spinner size={SpinnerSize.xSmall} />
                            <span style={{ color: '#666', fontStyle: 'italic' }}>Loading members...</span>
                        </div>
                    );
                }

                if (item.error) {
                    return (
                        <span style={{ color: '#d13438', fontStyle: 'italic' }}>
                            {item.error}
                        </span>
                    );
                }

                if (item.isLargeGroup) {
                    return (
                        <div style={{ padding: '4px 0', color: '#666', fontStyle: 'italic' }}>
                            Members not loaded due to large group size ({(item.actualMemberCount || 0).toLocaleString()} members).
                            Use the search feature below to check specific users.
                        </div>
                    );
                }

                if (item.members.length === 0) {
                    return (
                        <span style={{ color: '#666', fontStyle: 'italic' }}>
                            {this.state.searchText ? 'No matching members' : 'No members found'}
                        </span>
                    );
                }

                return (
                    <div style={{ padding: '4px 0' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {item.members.map((member, index) => {
                                const { searchText } = this.state;
                                const isHighlighted = searchText &&
                                    (member.text?.toLowerCase().includes(searchText.toLowerCase()) ||
                                        member.secondaryText?.toLowerCase().includes(searchText.toLowerCase()));

                                return (
                                    <span key={member.secondaryText || member.text || index}>
                                        {BeautyPerson({displayName:member.text || '', userPrincipalName:member.secondaryText || ''})}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                );
            }
        },
        {
            key: 'status',
            name: 'Status',
            ariaLabel: 'Loading Status',
            minWidth: 80,
            maxWidth: 100,
            onColumnClick: undefined,
            onRender: (item: IGroupWithMembers) => {
                if (item.isLoading) {
                    return <Spinner size={SpinnerSize.xSmall} label="Loading" />;
                }
                if (item.error) {
                    return <span style={{ color: '#d13438' }}>Error</span>;
                }
                return <span style={{ color: '#107c10' }}>✓ Loaded</span>;
            }
        }
    ];

    private renderLargeGroupSearch = (): JSX.Element | null => {
        const {
            isLargeGroupSearchEnabled,
            largeGroupSearchText,
            isSearchingLargeGroup,
            largeGroupSearchResult,
            groups
        } = this.state;

        const largeGroup = groups.find(g => g.isLargeGroup);
        if (!largeGroup) return null;

        return (
            <div style={{
                backgroundColor: '#fff8e1',
                border: '1px solid #ffcc02',
                borderRadius: '4px',
                padding: '12px',
                marginBottom: '16px'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                    <Toggle
                        label="Search Large Group Members"
                        checked={isLargeGroupSearchEnabled}
                        onChange={(_, checked) => this.setState({
                            isLargeGroupSearchEnabled: !!checked,
                            largeGroupSearchResult: { searchedUser: '', isMember: null, hasSearched: false }
                        })}
                    />
                </div>

                {isLargeGroupSearchEnabled && (
                    <div>
                        <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>
                            Check if a user is member of "{largeGroup.group.displayName}"
                            ({(largeGroup.actualMemberCount || 0).toLocaleString()} members)
                        </div>

                        <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                            <TextField
                                underlined
                                placeholder="Enter user email or UPN (e.g., user@domain.com)"
                                value={largeGroupSearchText}
                                onChange={(_, newValue) => this.setState({ largeGroupSearchText: newValue || '' })}
                                styles={{ root: { flex: 1 } }}
                                disabled={isSearchingLargeGroup}
                            />
                            <PrimaryButton
                                text="Check Membership"
                                onClick={this.searchLargeGroupMembership}
                                disabled={!largeGroupSearchText.trim() || isSearchingLargeGroup}
                                iconProps={isSearchingLargeGroup ? undefined : { iconName: 'Search' }}
                            />
                        </div>

                        {isSearchingLargeGroup && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Spinner size={SpinnerSize.xSmall} />
                                <span>Checking membership...</span>
                            </div>
                        )}

                        {largeGroupSearchResult.hasSearched && (
                            <div style={{
                                padding: '8px',
                                backgroundColor: largeGroupSearchResult.isMember === true ? '#e8f5e8' : '#ffeaea',
                                border: `1px solid ${largeGroupSearchResult.isMember === true ? '#4caf50' : '#f44336'}`,
                                borderRadius: '4px',
                                marginTop: '8px'
                            }}>
                                <div style={{ fontWeight: '600' }}>
                                    Search Result for: {largeGroupSearchResult.searchedUser}
                                </div>
                                <div style={{ marginTop: '4px' }}>
                                    {largeGroupSearchResult.isMember === true && (
                                        <span style={{ color: '#2e7d32' }}>
                                            ✓ User IS a member of {largeGroup.group.displayName}
                                        </span>
                                    )}
                                    {largeGroupSearchResult.isMember === false && (
                                        <span style={{ color: '#d32f2f' }}>
                                            ✗ User is NOT a member of {largeGroup.group.displayName}
                                        </span>
                                    )}
                                    {largeGroupSearchResult.isMember === null && (
                                        <span style={{ color: '#ff9800' }}>
                                            ⚠ Could not determine membership (user may not exist or access denied)
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    private renderSearchResults = (): JSX.Element | null => {
        const { searchResults, searchText } = this.state;

        if (!searchResults.hasSearch || !searchText) return null;

        return (
            <div style={{
                backgroundColor: '#f8f9fa',
                border: '1px solid #edebe9',
                borderRadius: '4px',
                padding: '12px',
                marginBottom: '16px'
            }}>
                <h4 style={{ margin: '0 0 8px 0', color: '#323130' }}>
                    Search Results for "{searchText}" (excluding large groups)
                </h4>
                {searchResults.foundUsers.length === 0 ? (
                    <div style={{ color: '#666', fontStyle: 'italic' }}>
                        No users found matching "{searchText}" in loaded groups
                    </div>
                ) : (
                    <div>
                        <div style={{ marginBottom: '8px', fontSize: '14px', color: '#666' }}>
                            Found {searchResults.foundUsers.length} user{searchResults.foundUsers.length > 1 ? 's' : ''}:
                        </div>
                        {searchResults.foundUsers.map((userResult, index) => (
                            <div key={index} style={{
                                backgroundColor: '#fff',
                                border: '1px solid #d2d0ce',
                                borderRadius: '4px',
                                padding: '8px',
                                marginBottom: '8px'
                            }}>
                                <div style={{ fontWeight: '600', marginBottom: '4px' }}>
                                    {userResult.user.text} ({userResult.user.secondaryText})
                                </div>
                                <div style={{ fontSize: '12px', color: '#666' }}>
                                    Member of {userResult.groups.length} group{userResult.groups.length > 1 ? 's' : ''}:
                                    <div style={{ marginTop: '4px' }}>
                                        {userResult.groups.map((group, groupIndex) => (
                                            <span
                                                key={groupIndex}
                                                style={{
                                                    display: 'inline-block',
                                                    backgroundColor: '#e1f5fe',
                                                    color: '#0277bd',
                                                    padding: '2px 6px',
                                                    margin: '2px 2px 2px 0',
                                                    borderRadius: '8px',
                                                    fontSize: '11px'
                                                }}
                                            >
                                                {group.group.displayName}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    private renderHeader = (): JSX.Element => {
        const { filteredGroups, groups, isLoadingGroups, isUsingCache, searchText } = this.state;
        const totalMembers = filteredGroups.reduce((sum, group) =>
            sum + (group.isLargeGroup ? (group.actualMemberCount || 0) : group.memberCount), 0
        );
        const loadingGroups = filteredGroups.filter(g => g.isLoading).length;
        const cacheAge = this.getCacheAge();

        return (
            <div style={{
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #edebe9',
                padding: '16px',
            }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                    <div>
                        <h3 style={{ margin: '0 0 8px 0' }}>FEBER Groups and Members</h3>
                        <div style={{ fontSize: '14px', color: '#666' }}>
                            {isLoadingGroups ? (
                                'Loading groups...'
                            ) : (
                                <>
                                    {searchText ? (
                                        <>Showing {filteredGroups.length} of {groups.length} groups • {totalMembers.toLocaleString()} members</>
                                    ) : (
                                        <>{groups.length} groups found • {totalMembers.toLocaleString()} total members</>
                                    )}
                                    {loadingGroups > 0 && (
                                        <span style={{ marginLeft: '8px', color: '#0078d4' }}>
                                            • Loading {loadingGroups} group{loadingGroups > 1 ? 's' : ''}...
                                        </span>
                                    )}
                                    {cacheAge && (
                                        <span style={{
                                            marginLeft: '8px',
                                            color: isUsingCache ? '#0078d4' : '#666',
                                            fontStyle: 'italic'
                                        }}>
                                            • {isUsingCache ? 'Cached data from' : 'Updated'} {cacheAge}
                                        </span>
                                    )}
                                </>
                            )}
                        </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        {isUsingCache && (
                            <CommandButton
                                iconProps={{ iconName: 'CloudDownload' }}
                                text="Load Fresh"
                                onClick={() => this.loadFeberGroups(false)}
                                styles={{
                                    root: { color: '#0078d4' },
                                    icon: { color: '#0078d4' }
                                }}
                            />
                        )}
                    </div>
                </div>

                {/* Search Boxes */}
                <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    {/* Group Name Search - Always visible */}
                    <div style={{ flex: '1', minWidth: '250px', maxWidth: '400px' }}>
                        <div style={{ 
                            marginBottom: '8px', 
                            fontSize: '13px', 
                            color: '#605e5c',
                            fontWeight: 500 
                        }}>
                            Search by group name:
                        </div>
                        <SearchBox
                            placeholder="Enter group name to filter..."
                            value={this.state.groupNameSearchText}
                            onChange={(_, newValue) => this.onGroupNameSearchChange(newValue)}
                            onClear={() => this.onGroupNameSearchChange('')}
                            styles={{
                                root: { width: '100%' }
                            }}
                            underlined={true}
                        />
                    </div>

                    {/* User Name Search - Toggle enabled */}
                    <div style={{ flex: '1', minWidth: '250px', maxWidth: '400px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                            <Toggle
                                label="Check which groups a user belongs to"
                                checked={this.state.isUserSearchEnabled}
                                onChange={(_, checked) => this.setState({
                                    isUserSearchEnabled: !!checked,
                                    searchText: '',
                                    searchResults: { foundUsers: [], hasSearch: false }
                                })}
                            />
                        </div>
                        {this.state.isUserSearchEnabled && (
                            <div>
                                <SearchBox
                                    placeholder="Enter user display name or email to find their groups..."
                                    value={searchText}
                                    onChange={(_, newValue) => this.onSearchChange(newValue)}
                                    onClear={() => this.onSearchChange('')}
                                    styles={{
                                        root: { width: '100%' }
                                    }}
                                    underlined={true}
                                />
                                <div style={{ 
                                    marginTop: '4px', 
                                    fontSize: '11px', 
                                    color: '#8a8886',
                                    fontStyle: 'italic' 
                                }}>
                                    Note: Search only checks user names. Large groups are excluded from this search.
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    render() {
        const { filteredGroups, isLoadingGroups, error, isUsingCache } = this.state;

        return (
            <div style={{margin:'1rem 2rem 0 0', height: '600px', display: 'block' }}>
                {error && (
                    <MessageBar
                        messageBarType={MessageBarType.error}
                        onDismiss={() => this.setState({ error: null })}
                        style={{ marginBottom: '16px' }}
                    >
                        {error}
                    </MessageBar>
                )}

                {isUsingCache && (
                    <MessageBar
                        messageBarType={MessageBarType.info}
                        style={{ marginBottom: '16px' }}
                    >
                        Showing cached data. Click "Load Fresh" to get the latest data from Azure AD.
                    </MessageBar>
                )}

                {this.renderHeader()}
                {this.renderLargeGroupSearch()}
                {this.renderSearchResults()}

                {isLoadingGroups ? (
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '200px'
                    }}>
                        <Spinner size={SpinnerSize.large} label="Loading FEBER groups..." />
                    </div>
                ) : filteredGroups.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                        <NoRecordsFound />
                        <p>{this.state.searchText ? `No groups or users found matching "${this.state.searchText}"` : 'No groups found with prefix "idm2bcd_feber"'}</p>
                    </div>
                ) : (
                    <div style={{
                        height: '400px',
                        overflow: 'auto',
                        border: '1px solid #edebe9',
                        marginTop: '0',
                        display: 'block'
                    }}>
                        <DetailsList
                            items={filteredGroups}
                            columns={this.groupColumns}
                            selectionMode={SelectionMode.none}
                            setKey="feberGroups"
                            layoutMode={DetailsListLayoutMode.justified}
                            isHeaderVisible={true}
                            selectionPreservedOnEmptyClick={true}
                        />
                    </div>
                )}
            </div>
        );
    }
}

export default ActualUserHasRoleTable;